/**
 * @file Turns a FormType's derive configuration into the row projections that fill
 * ONE ArTable, each carrying every column it fills and the phrase that column's
 * value comes from. Consumers: `useArTableDeriveProjections`, which owns the
 * subscriptions and publishes the result on the table's UI state.
 * Pure — every label arrives through `FormDeriveLabelResolvers`, so a source table
 * whose document has not landed yet renders its raw id rather than blocking.
 * Invariants: `FormDeriveEntry` is NOT narrowable from `source.kind`, so a row
 * projection is recognized by `'columns' in entry`; a declared column may carry the
 * dotted `parent.sub` address of a composite sub-field, split here at the FIRST dot
 * exactly as `arFormulaTargetFieldSegments` splits it; and every projected row is
 * reorderable, because the mirror arm that pinned row order to a source table is gone.
 */

import { describeFormDeriveRowValue, formDeriveCopy } from '@/services/formDerives';
import type { FormDeriveLabelResolvers } from '@/services/formDerives';
import type { ArTableId, FormDeriveEntry, FormDeriveId, TypeId } from '#types';

/** One column of this table a projection fills and locks on the rows it owns. */
type ArTableDeriveProjectionColumn = {
  /** The column the write lands on: the root of a dotted declared address. */
  typeId: TypeId;
  /** The composite sub-field a dotted address fills, or `null` for the whole cell. */
  subFieldId: string | null;
  /** Where this column's value comes from: "Manufacturer", "Sum of Line total". */
  sourcePhrase: string;
};

/** One row-projection derive owning rows of the table it is resolved against. */
type ArTableDeriveProjection = {
  /** The identity `formDeriveIdOfRowId` reads out of every row this derive owns. */
  deriveId: FormDeriveId;
  /** The label of the ONE table every column of this projection reads. */
  sourceTable: string;
  /**
   * Every column of this table the projection fills, in configuration order.
   * Readonly so a Valtio snapshot of a published projection stays assignable to
   * this type — the consumers all read it through `useSnapshot`.
   */
  columns: readonly ArTableDeriveProjectionColumn[];
  /** Why the row menu's Delete is disabled on a row this derive owns. */
  rowDeleteBlocked: string;
};

type ResolveArTableDeriveProjectionsInput = {
  /** Only entries TARGETING this table project rows into it. */
  tableId: ArTableId;
  /** Validated entries in config order, from `createFormDeriveEngineConfig`. */
  entries: readonly FormDeriveEntry[];
  /** Table, column and field labels; an id it cannot place renders raw. */
  labels: FormDeriveLabelResolvers;
};

function resolveArTableDeriveProjections(
  input: ResolveArTableDeriveProjectionsInput,
): ArTableDeriveProjection[] {
  const { tableId, entries, labels } = input;

  return entries.flatMap((entry): ArTableDeriveProjection[] => {
    if (!('columns' in entry)) return [];
    const { source, target } = entry;
    if (target.tableId !== tableId) return [];

    const sourceTable = labels.tableLabel(source.tableId) ?? source.tableId;
    /**
     * What the person removes to make the row go away. One row exists per distinct
     * value of the partition key, so that column's own name is what a person would
     * go looking for in the source table.
     */
    const keyColumn = labels.columnLabel(source.tableId, source.typeId) ?? source.typeId;

    return [{
      deriveId: entry.deriveId,
      sourceTable,
      columns: entry.columns.map((column): ArTableDeriveProjectionColumn => {
        const separator = column.typeId.indexOf('.');
        return {
          typeId: separator === -1 ? column.typeId : column.typeId.slice(0, separator),
          subFieldId: separator === -1 ? null : column.typeId.slice(separator + 1),
          sourcePhrase: describeFormDeriveRowValue(column.value, source.tableId, labels),
        };
      }),
      rowDeleteBlocked: formDeriveCopy.managedRowDeleteBlocked(
        keyColumn.toLowerCase(),
        sourceTable,
      ),
    }];
  });
}

export { resolveArTableDeriveProjections };
export type {
  ArTableDeriveProjection,
  ArTableDeriveProjectionColumn,
  ResolveArTableDeriveProjectionsInput,
};
